import React, { useState, useEffect, useRef } from 'react';
import './Registration.css';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useNavigate, NavLink, useLocation } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff, Heart, Menu, X, Upload, Camera, CheckCircle2, AlertCircle } from 'lucide-react';
import { format } from "date-fns";
import { DayPicker } from 'react-day-picker';
import { trackAuth, trackFormSubmission } from '../../Components/Utils/analytics';
import 'react-day-picker/dist/style.css';
import AvatarEditor from 'react-avatar-edit';
import { ayreneService } from '../../services/ayrene.service';
import image65 from '../img/img-65.jpg'
import fervoirLogo from '../img/Fervoir-Logo.png';
import countriesData from '../../Data/countries.json';
import MobileRegistration from './MobileRegistration';
import Footer from '../Footer/Footer';

interface FormInputs {
    FirstName: string;
    Email: string;
    RegisteredBy: string;
    Gender: string;
    DateOfBirth: string;
    Country: string; // Only country is needed for registration
    Religion: string;
    Password: string;
    ConfirmPassword: string;
}

const Registration: React.FC = () => {
    const navigateTo = useNavigate();
    const [profileImage, setProfileImage] = useState<File | null>(null);
    const [profileImagePreview, setProfileImagePreview] = useState<string | null>(null);
    const location = useLocation();
    const { register, handleSubmit, watch, setValue, clearErrors, formState: { errors } } = useForm<FormInputs>();
    const password = watch("Password");
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [dob, setDob] = useState<Date | null>(null);
    const [showVerifyModal, setShowVerifyModal] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const calendarContainerRef = useRef<HTMLDivElement>(null);
    const [showCustomCountry, setShowCustomCountry] = useState(false);
    const [customCountry, setCustomCountry] = useState('');
    const [showCropModal, setShowCropModal] = useState(false);
    const [croppedImage, setCroppedImage] = useState<string | null>(null);
    const [selectedImageSrc, setSelectedImageSrc] = useState<string | null>(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [scrollOpacity, setScrollOpacity] = useState(1);
    const [headerOpacity, setHeaderOpacity] = useState(1);
    const [isMobileView, setIsMobileView] = useState(false);
    const [showMobileRegistration, setShowMobileRegistration] = useState(false);
    const [showWelcomeScreen, setShowWelcomeScreen] = useState(true);
    const [isDragging, setIsDragging] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const checkMobileView = () => {
            setIsMobileView(window.innerWidth <= 1024);
        };
        checkMobileView();
        window.addEventListener('resize', checkMobileView);
        return () => window.removeEventListener('resize', checkMobileView);
    }, []);


    const countryValue = watch('Country');

    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY;
            const fadeDistance = 300;
            const newOpacity = Math.max(0, 1 - scrollPosition / fadeDistance);
            setScrollOpacity(newOpacity);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const validateFile = (file: File): string | null => {
        const maxSize = 5 * 1024 * 1024;
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

        if (!allowedTypes.includes(file.type)) {
            return 'Please upload a valid image file (JPG, PNG, or WebP)';
        }
        if (file.size > maxSize) {
            return 'File size must be less than 5MB';
        }
        return null;
    };

    const resizeImage = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        reject(new Error('Could not get canvas context'));
                        return;
                    }

                    const maxDimension = 1200;
                    let width = img.width;
                    let height = img.height;

                    if (width > maxDimension || height > maxDimension) {
                        if (width > height) {
                            height = (height / width) * maxDimension;
                            width = maxDimension;
                        } else {
                            width = (width / height) * maxDimension;
                            height = maxDimension;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;
                    ctx.drawImage(img, 0, 0, width, height);
                    resolve(canvas.toDataURL(file.type));
                };
                img.onerror = () => reject(new Error('Failed to load image'));
                img.src = e.target?.result as string;
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsDataURL(file);
        });
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const error = validateFile(file);

            if (error) {
                setUploadError(error);
                setTimeout(() => setUploadError(null), 3000);
                return;
            }

            setUploadError(null);
            setIsUploading(true);

            try {
                const resizedImageUrl = await resizeImage(file);
                setSelectedImageSrc(resizedImageUrl);
                setTimeout(() => {
                    setIsUploading(false);
                    setShowCropModal(true);
                }, 300);
            } catch (error) {
                setIsUploading(false);
                setUploadError('Failed to process image');
                setTimeout(() => setUploadError(null), 3000);
            }
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files && files[0]) {
            const file = files[0];
            const error = validateFile(file);

            if (error) {
                setUploadError(error);
                setTimeout(() => setUploadError(null), 3000);
                return;
            }

            setUploadError(null);
            setIsUploading(true);

            try {
                const resizedImageUrl = await resizeImage(file);
                setSelectedImageSrc(resizedImageUrl);
                setTimeout(() => {
                    setIsUploading(false);
                    setShowCropModal(true);
                }, 300);
            } catch (error) {
                setIsUploading(false);
                setUploadError('Failed to process image');
                setTimeout(() => setUploadError(null), 3000);
            }
        }
    };



    const handleRegistration: SubmitHandler<FormInputs> = async (data) => {
        setLoading(true);
        try {
            const payload = {
                FirstName: data.FirstName,
                Email: data.Email,
                Gender: data.Gender,
                DateOfBirth: format(dob as Date, "yyyy-MM-dd"),
                Password: data.Password,
                RegisteredBy: data.RegisteredBy,
                Country: data.Country,
                Religion: data.Religion || ''
            };

            console.log("Payload being sent:", payload);

            if (profileImage) {
                const formData = new FormData();
                Object.entries(payload).forEach(([key, value]) => {
                    formData.append(key, value);
                });
                formData.append('ProfilePicture', profileImage);

                console.log("=== FORM DATA CONTENT ===");
                formData.forEach((value, key) => console.log(`${key}: ${value}`));

                await axios.post("https://nrimarriage.in/api/v1/users/Registration", formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            } else {
                console.log("Sending JSON payload");
                await axios.post("https://nrimarriage.in/api/v1/users/Registration", payload, {
                    headers: { 'Content-Type': 'application/json' }
                });
            }

            setShowVerifyModal(true);
            trackAuth('register');
            trackFormSubmission('user_registration');

            try {
                const ayreneResult = await ayreneService.register({
                    email: data.Email,
                    password: data.Password,
                    username: data.FirstName
                });

                if (ayreneResult.success) {
                    console.log('User successfully registered on Ayrene chat platform');
                    localStorage.setItem('ayreneRegistered', 'true');
                } else {
                    console.warn('Ayrene registration failed, but Fervoir registration succeeded:', ayreneResult.message);
                    localStorage.setItem('ayreneRegistered', 'false');
                }
            } catch (ayreneErr) {
                console.error('Ayrene registration error (non-blocking):', ayreneErr);
                localStorage.setItem('ayreneRegistered', 'false');
            }
        } catch (err: any) {
            console.error("=== REGISTRATION ERROR DEBUG ===");
            console.error("Full error object:", err);
            console.error("Error response status:", err.response?.status);
            console.error("Error response data:", err.response?.data);
            console.error("Validation errors:", err.response?.data?.errors);
            console.error("Error response headers:", err.response?.headers);
            console.error("Request headers:", err.config?.headers);
            console.error("=== END DEBUG ===");

            let errorMessage = "An error occurred during registration.";
            if (err.response?.data?.errors) {
                const errors = err.response.data.errors;
                const errorMessages = [];
                for (const field in errors) {
                    if (Array.isArray(errors[field])) {
                        errorMessages.push(...errors[field]);
                    } else {
                        errorMessages.push(errors[field]);
                    }
                }
                if (errorMessages.length > 0) {
                    errorMessage = errorMessages.join(', ');
                }
            } else if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            }
            alert(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleMobileSubmit = async (mobileFormData: any) => {
        setLoading(true);
        try {
            const payload = {
                FirstName: mobileFormData.FirstName,
                Email: mobileFormData.Email,
                Gender: mobileFormData.Gender,
                DateOfBirth: mobileFormData.DateOfBirth,
                Password: mobileFormData.Password,
                RegisteredBy: mobileFormData.RegisteredBy,
                Country: mobileFormData.Country,
                Religion: mobileFormData.Religion || ''
            };

            if (mobileFormData.ProfilePicture) {
                const formData = new FormData();
                Object.entries(payload).forEach(([key, value]) => {
                    formData.append(key, value);
                });
                formData.append('ProfilePicture', mobileFormData.ProfilePicture);

                await axios.post("https://nrimarriage.in/api/v1/users/Registration", formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            } else {
                await axios.post("https://nrimarriage.in/api/v1/users/Registration", payload, {
                    headers: { 'Content-Type': 'application/json' }
                });
            }

            setShowVerifyModal(true);
            setShowMobileRegistration(false);
            trackAuth('register');
            trackFormSubmission('user_registration');

            try {
                const ayreneResult = await ayreneService.register({
                    email: mobileFormData.Email,
                    password: mobileFormData.Password,
                    username: mobileFormData.FirstName
                });

                if (ayreneResult.success) {
                    console.log('User successfully registered on Ayrene chat platform');
                    localStorage.setItem('ayreneRegistered', 'true');
                } else {
                    console.warn('Ayrene registration failed, but Fervoir registration succeeded:', ayreneResult.message);
                    localStorage.setItem('ayreneRegistered', 'false');
                }
            } catch (ayreneErr) {
                console.error('Ayrene registration error (non-blocking):', ayreneErr);
                localStorage.setItem('ayreneRegistered', 'false');
            }
        } catch (err: any) {
            let errorMessage = "An error occurred during registration.";
            if (err.response?.data?.errors) {
                const errors = err.response.data.errors;
                const errorMessages = [];
                for (const field in errors) {
                    if (Array.isArray(errors[field])) {
                        errorMessages.push(...errors[field]);
                    } else {
                        errorMessages.push(errors[field]);
                    }
                }
                if (errorMessages.length > 0) {
                    errorMessage = errorMessages.join(', ');
                }
            } else if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            }
            alert(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const passwordValidationRules = {
        required: "Password is required",
        minLength: {
            value: 8,
            message: "Password must be at least 8 characters long",
        },
        pattern: {
            value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
            message: "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character.",
        },
    };

    useEffect(() => {
        if (dob) {
            setValue("DateOfBirth", format(dob, "yyyy-MM-dd"));
        }
    }, [dob, setValue]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (calendarContainerRef.current && !calendarContainerRef.current.contains(event.target as Node)) {
                setShowCalendar(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);


    // Handle crop events
    const onCrop = (preview: string) => {
        setCroppedImage(preview);
    };

    const onClose = () => {
        setCroppedImage(null);
    };

    const onBeforeFileLoad = (elem: React.ChangeEvent<HTMLInputElement>) => {
        const file = elem.target.files?.[0];
        if (file && file.size > 2 * 1024 * 1024) { // Limit: 2MB
            alert("File is too big! Max size is 2MB.");
            elem.target.value = "";
        }
    };

    const handleCropCancel = () => {
        if (selectedImageSrc) URL.revokeObjectURL(selectedImageSrc);
        setSelectedImageSrc(null);
        setShowCropModal(false);
    };

    const handleCropSave = () => {
        if (croppedImage) {
            fetch(croppedImage)
                .then(res => res.blob())
                .then(blob => {
                    const file = new File([blob], "profile.jpg", { type: "image/jpeg" });
                    setProfileImage(file);
                    setProfileImagePreview(croppedImage);
                    if (selectedImageSrc) URL.revokeObjectURL(selectedImageSrc);
                    setSelectedImageSrc(null);
                    setShowCropModal(false);
                });
        }
    };



    if (isMobileView && showMobileRegistration) {
        return (
            <MobileRegistration
                onSubmit={handleMobileSubmit}
                onClose={() => {
                    setShowMobileRegistration(false);
                    setShowWelcomeScreen(true);
                }}
                loading={loading}
            />
        );
    }

    if (isMobileView && showWelcomeScreen) {
        return (
            <div className="registration-container">
                <div className="floating-shapes">
                    <Heart className="shape heart" size={40} />
                    <Heart className="shape heart-2" size={32} />
                    <Heart className="shape heart-3" size={36} />
                </div>

                <header className="registration-header">
                    <div className="header-content">
                        <div className="logo-section">
                            <h1 className="tinder-logo1">
                                <img src={fervoirLogo} alt="Fervoir logo" className="fervoir-logo-img" />
                                Fervoir
                            </h1>
                        </div>

                        <nav className={`nav-menu ${mobileMenuOpen ? 'mobile-open' : ''}`}>
                            <a href="#products" className="nav-link2">Products</a>
                            <a href="#learn" className="nav-link2">Learn</a>
                            <a href="#safety" className="nav-link2">Safety</a>
                            <a href="#support" className="nav-link2">Support</a>
                            <a href="#download" className="nav-link2">Download</a>
                        </nav>

                        <div className="header-actions">
                            <button className="login-header-btn" onClick={() => navigateTo('/login')}>
                                Log in
                            </button>
                            <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                            </button>
                        </div>
                    </div>
                </header>

                <main className="welcome-screen-main">
                    <div className="welcome-content">
                        <div className="welcome-hero">
                            <h1 className="welcome-title">Turn sparks into stories.</h1>
                            <p className="welcome-subtitle">Join millions finding their perfect match</p>
                            <p className="welcome-description">
                                Start your journey to find meaningful connections. Create your profile in just a few simple steps.
                            </p>
                        </div>

                        <button
                            className="get-started-btn"
                            onClick={() => {
                                setShowWelcomeScreen(false);
                                setShowMobileRegistration(true);
                            }}
                        >
                            Get Started
                        </button>

                        <div className="welcome-features">
                            <div className="feature-item">
                                <div className="feature-icon">💝</div>
                                <p>Find Your Match</p>
                            </div>
                            <div className="feature-item">
                                <div className="feature-icon">🔒</div>
                                <p>Safe & Secure</p>
                            </div>
                            <div className="feature-item">
                                <div className="feature-icon">⚡</div>
                                <p>Quick Setup</p>
                            </div>
                        </div>

                        <div className="welcome-footer-text">
                            <span>Already have an account? </span>
                            <button
                                className="welcome-login-link"
                                onClick={() => navigateTo('/login')}
                            >
                                Sign in
                            </button>
                        </div>
                    </div>
                </main>

                <Footer />
            </div>
        );
    }

    return (
        <div className="registration-container">
            <div className="floating-shapes">
                <Heart className="shape heart" size={40} />
                <Heart className="shape heart-2" size={32} />
                <Heart className="shape heart-3" size={36} />
            </div>

            <header className="registration-header" style={{ opacity: headerOpacity }}>
                <div className="header-content">
                    <div className="logo-section">
                        <h1 className="tinder-logo1">
                            <img src={fervoirLogo} alt="Fervoir logo" className="fervoir-logo-img" />
                            Fervoir
                        </h1>
                    </div>

                    <nav className={`nav-menu ${mobileMenuOpen ? 'mobile-open' : ''}`}>
                        <a href="#products" className="nav-link2">Products</a>
                        <a href="#learn" className="nav-link2">Learn</a>
                        <a href="#safety" className="nav-link2">Safety</a>
                        <a href="#support" className="nav-link2">Support</a>
                        <a href="#download" className="nav-link2">Download</a>
                    </nav>

                    <div className="header-actions">
                        <button className="login-header-btn" onClick={() => navigateTo('/login')}>
                            Log in
                        </button>
                        <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                        </button>
                    </div>
                </div>
            </header>

            <main className="registration-main">
                <div className="hero-section" style={{ opacity: scrollOpacity, transform: `translateY(${(1 - scrollOpacity) * -20}px)` }}>
                    <div className="hero-content">
                        <h1 className="hero-title">Turn sparks into stories.</h1>
                        <p className="hero-subtitle">Join millions finding their perfect match</p>
                        <div className="hero-image">
                            <img src={image65} alt="Couple" />
                            <div className="image-overlay"></div>
                        </div>
                    </div>
                </div>

                <div className="registration-form-container">
                    <form onSubmit={handleSubmit(handleRegistration)} className="registration-form" style={{ pointerEvents: showVerifyModal ? 'none' : 'auto', opacity: showVerifyModal ? 0.5 : 1 }}>
                        <h2>Create Account</h2>
                        <p className="form-subtitle">Start your journey to find love</p>

                        <div className="form-group10 profile-upload-container">
                            <div
                                className={`profile-upload-wrapper ${isDragging ? 'dragging' : ''} ${isUploading ? 'uploading' : ''}`}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                            >
                                <input
                                    type="file"
                                    accept="image/jpeg,image/jpg,image/png,image/webp"
                                    id="profilePicture"
                                    ref={fileInputRef}
                                    className="profile-upload-input"
                                    onChange={handleFileChange}
                                />
                                <label htmlFor="profilePicture" className="profile-upload-label">
                                    <div className={`profile-upload-circle ${profileImagePreview ? 'has-image' : ''}`}>
                                        {isUploading ? (
                                            <div className="upload-spinner">
                                                <div className="spinner"></div>
                                            </div>
                                        ) : profileImagePreview ? (
                                            <>
                                                <img
                                                    src={profileImagePreview}
                                                    alt="Profile Preview"
                                                    className="profile-image-preview"
                                                />
                                                <div className="image-overlay">
                                                    <Camera size={28} className="change-icon" />
                                                    <span className="change-text">Change</span>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="upload-content">
                                                <Camera size={40} className="upload-camera-icon" />
                                                <span className="upload-text-main">Add Photo</span>
                                            </div>
                                        )}
                                    </div>
                                </label>
                                {uploadError && (
                                    <div className="upload-error">
                                        <AlertCircle size={16} />
                                        <span>{uploadError}</span>
                                    </div>
                                )}
                                {profileImagePreview && !uploadError && (
                                    <div className="upload-success">
                                        <CheckCircle2 size={16} />
                                        <span>Photo added</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="form-group10">
                            <input
                                type="text"
                                placeholder="First Name"
                                {...register('FirstName', { required: "First Name is required" })}
                            />
                            {errors.FirstName && <span className="error">{errors.FirstName.message}</span>}
                        </div>

                        <div className="form-group10" style={{ marginBottom: "15px" }}>
                            <input
                                type="email"
                                placeholder="Email Address"
                                {...register('Email', { required: "Email is required" })}
                            />
                            {errors.Email && <span className="error">{errors.Email.message}</span>}
                        </div>

                        <div className="form-grid">
                            <div className="form-group10">
                                <select {...register('RegisteredBy', { required: "This field is required" })}>
                                    <option value="">Registered By</option>
                                    <option value="Self">Self</option>
                                    <option value="Guardian">Guardian</option>
                                </select>
                                {errors.RegisteredBy && <span className="error">{errors.RegisteredBy.message}</span>}
                            </div>

                            <div className="form-group10">
                                <select {...register('Gender', { required: "Gender is required" })}>
                                    <option value="">Select Gender</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                </select>
                                {errors.Gender && <span className="error">{errors.Gender.message}</span>}
                            </div>
                        </div>

                        <div className="form-group10" ref={calendarContainerRef}>
                            <input
                                type="text"
                                placeholder="yyyy-MM-dd"
                                value={dob ? format(dob, 'yyyy-MM-dd') : ''}
                                readOnly
                                onClick={() => setShowCalendar(true)}
                                onFocus={() => setShowCalendar(true)}
                                className="custom-datepicker"
                            />
                            {showCalendar && (
                                <div className="datepicker-popover">
                                    <DayPicker
                                        mode="single"
                                        selected={dob as Date}
                                        onSelect={(date) => {
                                            setDob(date ?? null);
                                            setShowCalendar(false);
                                        }}
                                        captionLayout="dropdown"
                                        fromYear={1900}
                                        toYear={new Date().getFullYear()}
                                    />
                                </div>
                            )}
                        </div>

                        <input
                            type="hidden"
                            {...register('DateOfBirth', { required: "Date of Birth is required" })}
                        />

                        <div className="form-grid">
                            <div className="form-group10">
                                <select
                                    value={showCustomCountry ? 'Others' : countryValue || ''}
                                    onChange={(e) => {
                                        if (e.target.value === 'Others') {
                                            setShowCustomCountry(true);
                                            setCustomCountry('');
                                            setValue('Country', '');
                                            clearErrors('Country');
                                        } else {
                                            setShowCustomCountry(false);
                                            setCustomCountry('');
                                            setValue('Country', e.target.value);
                                            clearErrors('Country');
                                        }
                                    }}
                                >
                                    <option value="">Select Country</option>
                                    {countriesData.map((country) => (
                                        <option key={country.name} value={country.name}>
                                            {country.name}
                                        </option>
                                    ))}
                                    <option value="Others">Others</option>
                                </select>

                                {/* Hidden input for form validation */}
                                <input
                                    type="hidden"
                                    {...register('Country', {
                                        required: "Country is required",
                                        validate: (value) => {
                                            if (showCustomCountry) {
                                                return customCountry.trim() !== '' || "Please enter your country";
                                            }
                                            return value !== '' || "Please select a country";
                                        }
                                    })}
                                    value={showCustomCountry ? customCountry : countryValue || ''}
                                />

                                {showCustomCountry && (
                                    <input
                                        type="text"
                                        placeholder="Enter your country"
                                        value={customCountry}
                                        onChange={(e) => {
                                            setCustomCountry(e.target.value);
                                            setValue('Country', e.target.value);
                                            clearErrors('Country');
                                        }}
                                        style={{ marginTop: '0.5rem' }}
                                    />
                                )}
                                {errors.Country && <span className="error">{errors.Country.message}</span>}
                            </div>

                            <div className="form-group10">
                                <select {...register('Religion')}>
                                    <option value="">Select Religion</option>
                                    <option value="Christian">Christian</option>
                                    <option value="Muslim">Muslim</option>
                                    <option value="Hindu">Hindu</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-group10 password-group">
                            <div className="password-input">
                                <input
                                    type={passwordVisible ? "text" : "password"}
                                    placeholder="Password"
                                    {...register('Password', passwordValidationRules)}
                                />
                                <button
                                    type="button"
                                    className="toggle-password"
                                    onClick={() => setPasswordVisible(!passwordVisible)}
                                >
                                    {passwordVisible ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                            {errors.Password && <span className="error">{errors.Password.message}</span>}
                        </div>

                        <div className="form-group10 password-group">
                            <div className="password-input">
                                <input
                                    type={confirmPasswordVisible ? "text" : "password"}
                                    placeholder="Confirm Password"
                                    {...register("ConfirmPassword", {
                                        required: "Confirm Password is required",
                                        validate: (value) => value === password || "Passwords do not match",
                                    })}
                                />
                                <button
                                    type="button"
                                    className="toggle-password"
                                    onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
                                >
                                    {confirmPasswordVisible ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                            {errors.ConfirmPassword && <span className="error">{errors.ConfirmPassword.message}</span>}
                        </div>

                        <button
                            type="submit"
                            className="submit-btn"
                            disabled={loading}
                            aria-busy={loading}
                        >
                            {loading ? "Processing..." : "Continue"}
                        </button>

                        <div className="form-footer">
                            <span className="footer-text">Already have an account? </span>
                            <NavLink to="/login" className="login-link">Sign in</NavLink>
                        </div>
                    </form>
                </div>
            </main>

            <Footer />

            {showVerifyModal && (
                <div className="verify-modal">
                    <div className="modal-content1">
                        <h3>🎉 Registration Successful</h3>
                        <p>Please check your email and click the verification link.</p>
                        <p>You'll be redirected to login after verifying.</p>
                    </div>
                </div>
            )}

            {/* Crop Modal */}
            {showCropModal && (
                <div className="crop-modal-overlay">
                    <div className="crop-modal">
                        <div className="crop-modal-header">
                            <h3>Crop Your Profile Picture</h3>
                            <button className="crop-close-btn" onClick={handleCropCancel}>×</button>
                        </div>
                        <div className="crop-modal-content">
                            <AvatarEditor
                                width={300}
                                height={300}
                                imageWidth={300}
                                onCrop={onCrop}
                                onClose={onClose}
                                onBeforeFileLoad={onBeforeFileLoad}
                                src={selectedImageSrc || ""}
                                shadingColor={'rgba(0,0,0,0.5)'}
                                backgroundColor={'#0a0a0a'}
                                closeIconColor={'#FFB900'}
                                shadingOpacity={0.7}
                                cropRadius={150}
                                cropColor={'#FFB900'}
                                lineWidth={4}
                            />

                        </div>
                        <div className="crop-modal-actions">
                            <button className="crop-cancel-btn" onClick={handleCropCancel}>
                                Cancel
                            </button>
                            <button
                                className="crop-save-btn"
                                onClick={handleCropSave}
                                disabled={!croppedImage}
                            >
                                Save Cropped Image
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default Registration;